import type { EngravingModalData } from '../../../core/src/lib/services/engraving-modal.service';
import {
  Component,
  EventEmitter,
  Input,
  type OnInit,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { ProductCart, ProductDetails } from '@models';
import { MatInputModule } from '@angular/material/input';
import { MatButton } from '@angular/material/button';

// Define a simplified interface for the data passed to the template
interface EngravingDisplayData {
  deviceName: string;
  image: { src: string; alt: string };
  maxCharactersPerLine: number;
}

@Component({
  selector: 'app-apple-engraving',
  imports: [FormsModule, MatInputModule, MatButton],
  templateUrl: './apple-engraving.component.html',
  styleUrls: ['./apple-engraving.component.scss'],
})
export class AppleEngravingComponent implements OnInit, AfterViewInit, OnDestroy {
  // --- INPUTS & OUTPUTS ---
  @Input() data: EngravingModalData | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<{ productId: number; engravingText: string }>();
  @Output() skip = new EventEmitter<void>();

  // --- VIEW CHILDREN ---
  @ViewChild('modalContent', { static: false }) modalContent!: ElementRef<HTMLElement>;
  @ViewChild('firstLineInput', { static: false }) firstLineInput!: ElementRef<HTMLInputElement>;
  @ViewChild('closeButton', { static: false }) closeButton!: ElementRef<HTMLButtonElement>;

  // --- STATE ---
  engravingText = {
    firstLine: '',
    secondLine: '',
  };
  displayData: EngravingDisplayData | null = null;
  private previousActiveElement: HTMLElement | null = null;
  private focusableElements: HTMLElement[] = [];

  get isDisabled(): boolean {
    return this.data?.isDisabled === true;
  }

  get existingEngraving(): string {
    return this.data?.existingEngraving || '';
  }

  // --- KEYBOARD EVENT HANDLERS ---
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.onClose();
      return;
    }

    if (event.key === 'Tab') {
      this.handleTabNavigation(event);
    }
  }

  ngOnInit(): void {
    // Store the currently focused element
    this.previousActiveElement = document.activeElement as HTMLElement;

    if (this.data) {
      const getImageUrl = (product: ProductCart | ProductDetails): string => {
        // First: Check if the product has productVariances
        if ('productVariances' in product && Array.isArray(product.productVariances)) {
          // Find the variance named "Engraving"
          const engravingVariance = product.productVariances.find(
            (variance) => variance.name?.toLowerCase() === 'engraving',
          );
          // Get the first option (or a specific one, if needed) with an imageUrl
          const engravingImageUrl = engravingVariance?.options?.[0]?.imageUrl;
          if (engravingImageUrl) {
            return engravingImageUrl;
          }
        }

        // Fallback to top-level imageUrl if Engraving-specific image is not found
        if ('imageUrl' in product && product.imageUrl) {
          return product.imageUrl;
        }
        if ('images' in product) {
          return product.images[0][0];
        }
        return ''; // Default fallback
      };

      const getMaxChars = (product: ProductCart | ProductDetails): number => {
        if ('maxCharactersPerLine' in product && typeof product.maxCharactersPerLine === 'number') {
          return product.maxCharactersPerLine;
        }
        return 15; // Default value if property doesn't exist
      };

      const extractIpadTitle = (name: string): string => {
        // Match "iPad" optionally followed by "Pro" or "Air"
        const match = name.match(/\biPad\b(?:\s+(Pro|Air))?/i);

        if (!match) return ''; // Not an iPad product

        // Capitalize first letter just in case input is not consistent
        const model = match[1] ? match[1][0].toUpperCase() + match[1].slice(1).toLowerCase() : '';
        return model ? `iPad ${model}` : 'iPad';
      };

      this.displayData = {
        deviceName: extractIpadTitle(this.data.product.name),
        image: {
          src: getImageUrl(this.data.product),
          alt: this.data.product.name,
        },
        maxCharactersPerLine: getMaxChars(this.data.product),
      };

      const engravingToUse = this.existingEngraving || this.data.initialEngraving || '';
      if (engravingToUse) {
        const lines = engravingToUse.split('\n');
        this.engravingText.firstLine = lines[0] || '';
        this.engravingText.secondLine = lines[1] || '';
      }
    }
  }

  ngAfterViewInit(): void {
    // Set up focus management after view is initialized
    setTimeout(() => {
      this.setupFocusManagement();
      this.focusFirstElement();
    });
  }

  ngOnDestroy(): void {
    // Restore focus to the previously focused element
    if (this.previousActiveElement && this.previousActiveElement.focus) {
      this.previousActiveElement.focus();
    }
  }

  private setupFocusManagement(): void {
    if (!this.modalContent) return;

    // Find all potentially focusable elements
    const focusableSelectors = [
      'button',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    // Filter out elements that are actually focusable (not disabled and visible)
    this.focusableElements = Array.from(this.modalContent.nativeElement.querySelectorAll(focusableSelectors)).filter(
      (element: Element) => {
        const htmlElement = element as HTMLElement;
        // Check if element is actually focusable
        if (htmlElement.hasAttribute('disabled')) return false;
        if (htmlElement.style.display === 'none') return false;
        if (htmlElement.style.visibility === 'hidden') return false;
        return true;
      },
    ) as HTMLElement[];
  }

  private focusFirstElement(): void {
    // Focus the modal container itself to avoid focusing inputs on open
    if (this.modalContent && this.modalContent.nativeElement) {
      this.modalContent.nativeElement.focus();
      return;
    }

    // Fallbacks if container is not available
    if (this.closeButton) {
      this.closeButton.nativeElement.focus();
      return;
    }

    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus();
    }
  }

  private handleTabNavigation(event: KeyboardEvent): void {
    // Update focusable elements dynamically to account for disabled state changes
    this.setupFocusManagement();

    if (this.focusableElements.length === 0) return;

    const currentFocusIndex = this.focusableElements.findIndex((element) => element === document.activeElement);

    // If current element is not in focusable list, start from beginning
    let startIndex = currentFocusIndex >= 0 ? currentFocusIndex : 0;

    if (event.shiftKey) {
      // Shift + Tab: Move to previous element
      event.preventDefault();
      const previousIndex = startIndex <= 0 ? this.focusableElements.length - 1 : startIndex - 1;
      this.focusableElements[previousIndex].focus();
    } else {
      // Tab: Move to next element
      event.preventDefault();
      const nextIndex = startIndex >= this.focusableElements.length - 1 ? 0 : startIndex + 1;
      this.focusableElements[nextIndex].focus();
    }
  }

  // --- EVENT HANDLERS ---
  onClose(): void {
    this.close.emit();
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  onSave(): void {
    if (this.data && this.canSave()) {
      const combinedText = [this.engravingText.firstLine.trim(), this.engravingText.secondLine.trim()]
        .filter(Boolean)
        .join('\n');

      this.save.emit({
        productId: this.data.product.productId,
        engravingText: combinedText,
      });
    }
  }

  onSkip(): void {
    this.skip.emit();
  }

  // --- VALIDATION & HELPERS ---
  isFirstLineValid(): boolean {
    const maxLength = this.displayData?.maxCharactersPerLine ?? 15;
    return this.engravingText.firstLine.trim().length > 0 && this.engravingText.firstLine.length <= maxLength;
  }

  isSecondLineValid(): boolean {
    const maxLength = this.displayData?.maxCharactersPerLine ?? 15;
    return this.engravingText.secondLine.length <= maxLength;
  }

  canSave(): boolean {
    return this.isFirstLineValid() && this.isSecondLineValid();
  }

  hasEngraving(): boolean {
    return !!(this.engravingText.firstLine || this.engravingText.secondLine);
  }

  getDisabledTitle(): string {
    if (!this.displayData) return '';

    const deviceName = this.displayData.deviceName;
    const specs = this.engravingText.firstLine
      ? `Wi-Fi 256GB with standard glass-Space Black`
      : 'Wi-Fi 256GB with standard glass-Space Black';

    return `${deviceName} ${specs}`;
  }
}
